import { PickupTicketStatus } from '@core/models/order.enum';
import { OrderGroupState, OrderState, OrderTotalsState, PickupTicketState } from '@core/models/order.state';
import { StoreState } from '@core/models/store.state';

import { seedPlatformConfig } from '../platform-config/platform-config.seed';

/**
 * Pure order-minting helpers shared by the checkout engine
 * (order.mock.repository.ts) and the auction close engine's winner
 * auto-charge (auction.mock.repository.ts, HLD D-05). Keeping the math and
 * ticket rules in one place guarantees both paths mint identical orders.
 */

/** Commerce policy the Admin Portal owns (ADM-02), not engine constants. */
const platformConfig = seedPlatformConfig();

/** Combined sales-tax rate applied to items + shipping (PAY-04). */
export const ORDER_TAX_RATE = platformConfig.salesTaxRate;

/** Fallback shipping ETA when a listing carries none. */
export const SHIPPING_ETA_FALLBACK = 'Arrives in 4-7 business days';

/** Pickup-code alphabet without ambiguous characters (0/O, 1/I). */
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ23456789';

/** The lowest order number the mock engines mint refs above. */
const ORDER_REF_FLOOR = 10482;

export const roundMoney = (amount: number): number => Math.round(amount * 100) / 100;

/** Mint a pickup ticket honoring the store's hold window (HLD §11, PKP-06). */
export function mintPickupTicket(store: StoreState | undefined): PickupTicketState {
  const holdDays = store?.pickupHoldDays ?? platformConfig.pickupHoldDays;
  const code = `${codeChunk(3)}-${codeChunk(3)}`;
  return {
    code,
    qrPayload: `brijjfinds:pickup:${code}`,
    status: PickupTicketStatus.AwaitingStaging,
    stagedAt: null,
    expiresAt: new Date(Date.now() + holdDays * 86_400_000),
    holdDays
  };
}

/** Order totals over minted groups - items + shipping taxed at the mock rate. */
export function buildOrderTotals(groups: readonly OrderGroupState[]): OrderTotalsState {
  const items = groups.reduce((sum, g) => sum + g.itemsSubtotal, 0);
  const shipping = groups.reduce((sum, g) => sum + g.shippingAmount, 0);
  const tax = roundMoney((items + shipping) * ORDER_TAX_RATE);
  return { items, shipping, tax, total: roundMoney(items + shipping + tax) };
}

/**
 * Next public order ref, derived from the highest ref already in the orders
 * collection so the checkout and auction-close engines never collide.
 */
export function nextOrderRef(orders: readonly OrderState[]): string {
  const highest = orders.reduce((max, order) => {
    const match = /^BF-(\d+)$/.exec(order.orderRef);
    return match ? Math.max(max, Number(match[1])) : max;
  }, ORDER_REF_FLOOR);
  return `BF-${highest + 1}`;
}

function codeChunk(length: number): string {
  let chunk = '';
  for (let i = 0; i < length; i++) {
    chunk += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
  }
  return chunk;
}
